import { AlertTriangle, Check, Info, Loader2, XCircle } from "lucide-react"

import { useToast } from "@/hooks/use-toast"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"

// Loaning Hub reference toast icons: green check on navy for success, amber
// warning on a brown ground. Error and info follow the same pattern.
const KIND_ICON = {
  success: { Icon: Check, className: "text-[#6fd99b]" },
  error: { Icon: XCircle, className: "text-[#f2877b]" },
  warning: { Icon: AlertTriangle, className: "text-[#f0b45e]" },
  info: { Icon: Info, className: "text-[#c6cfe0]" },
  loading: { Icon: Loader2, className: "text-[#c6cfe0] animate-spin motion-reduce:animate-none" },
}

// Callers also pass the status as `variant` ("success", "warning", ...). Only
// default / destructive / warning are toast variants; anything else used to reach
// <Toast> unstyled, leaving white text on no background. It now sets the icon here
// and falls back to the default navy pill below.
const VARIANT_KIND = { destructive: "error", success: "success", warning: "warning", info: "info" }
const TOAST_VARIANTS = new Set(["default", "destructive", "warning"])

export const resolveKind = (kind, variant) => kind || VARIANT_KIND[variant] || null

export const resolveToastVariant = (kind, variant) => {
  const resolved = resolveKind(kind, variant)
  if (resolved === "warning") return "warning"
  return TOAST_VARIANTS.has(variant) ? variant : "default"
}

export function Toaster() {
  const { toasts } = useToast()

  return (
    // Reference keeps toasts ~2.6s; 4s leaves time to read longer messages.
    <ToastProvider duration={4000} swipeDirection="down">
      {toasts.map(function ({ id, title, description, action, kind, variant, ...props }) {
        const resolved = resolveKind(kind, variant)
        const icon = resolved ? KIND_ICON[resolved] : null
        const toastVariant = resolveToastVariant(kind, variant)
        return (
          <Toast key={id} variant={toastVariant} {...props}>
            {icon && (
              <icon.Icon
                className={`h-4 w-4 flex-none ${icon.className}`}
                strokeWidth={2.4}
                aria-hidden="true"
              />
            )}
            <div className="grid min-w-0 gap-0.5">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && (
                <ToastDescription>{description}</ToastDescription>
              )}
            </div>
            {action}
            <ToastClose aria-label="Dismiss notification" />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
